import { Search } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { EmptyState } from '@/components/EmptyState';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { useAuth } from '@/hooks/useAuth';
import { can, actingOrgUnitTypeOf } from '@/lib/accessRole';
import { visibleDestinations } from '@/lib/navigation';
import { MOD_KEY_LABEL } from '@/lib/platform';
import { cn } from '@/lib/utils';

/**
 * THE ⌘K AFFORDANCE — and it opens something real.
 *
 * The shape is the reference product's: an outline BUTTON rather than an input, so the
 * shortcut hint has somewhere to live and the utility bar keeps one focus stop.
 *
 * WHAT SHIPS: navigating to any destination the officer's capabilities allow, and handing
 * a query to the officer list. That second one works with no new API because
 * `useServerTable` reads `search` from the URL through nuqs — so `/officers?search=roy`
 * arrives pre-filtered and is a shareable link.
 *
 * WHAT IS DEFERRED, named so nobody assumes otherwise: cross-entity server-side search,
 * recent items, command ACTIONS ("Create officer"), fuzzy ranking, and roving-focus arrow
 * navigation. That last set is where `cmdk` would earn its dependency; today it would not.
 */
export const GlobalSearch = () => {
  const { officer } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      // Both modifiers on every platform: the glyph is cosmetic, the binding is not.
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setOpen((previous) => !previous);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  /* ⚠️ [C5b §5] The flags too — a palette that ignored them would offer a page the sidebar hides. */
  /* ⚠️ [C5c §3] And the seat, for the same reason — the Policies row follows `mayAuthorPolicy`. */
  const actingType = actingOrgUnitTypeOf(officer);
  const destinations = useMemo(
    () => visibleDestinations(officer?.accessRole, officer?.flags, actingType),
    [officer?.accessRole, officer?.flags, actingType],
  );
  const trimmed = query.trim();

  const matches = useMemo(
    () => destinations.filter((entry) => entry.label.toLowerCase().includes(trimmed.toLowerCase())),
    [destinations, trimmed],
  );

  const canSearchOfficers = Boolean(officer && can(officer.accessRole, 'officer.read') && trimmed);

  const go = (to: string) => {
    setOpen(false);
    setQuery('');
    void navigate(to);
  };

  return (
    <>
      <Button
        variant="outline"
        className="h-[var(--control-height)] w-full max-w-sm justify-start gap-2 text-muted-foreground"
        onClick={() => setOpen(true)}
        aria-keyshortcuts="Control+K Meta+K"
        data-testid="global-search"
      >
        <Search aria-hidden />
        <span className="truncate text-sm">Search…</span>
        <kbd className="ml-auto hidden rounded border border-border bg-secondary px-1.5 text-[10px] text-muted-foreground sm:inline">
          {MOD_KEY_LABEL}K
        </kbd>
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="gap-0 p-0 sm:max-w-lg">
          {/* Radix requires a title for the accessible name; the input is the visible one. */}
          <DialogTitle className="sr-only">Search</DialogTitle>

          {/*
            ⚠️ [C1h §2c] THE GLYPHS ARE `text-illustration`, WHICH IS `text-subtle` IN
            `standard` — so this palette renders identically and `colourful` picks up the brand
            hue for free. They are the right home for it: `aria-hidden`, purely decorative, and
            in a surface with no numbers in it at all, which is precisely what §2c colours and
            §2g protects everywhere else.
          */}
          <div className="flex items-center gap-2 px-3 py-2">
            <Search className="size-4 shrink-0 text-illustration" aria-hidden />
            <Input
              autoFocus
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={(event) => {
                if (event.key !== 'Enter') return;
                const first = matches[0];
                if (first) go(first.to);
                else if (canSearchOfficers) go(`/officers?search=${encodeURIComponent(trimmed)}`);
              }}
              placeholder="Go to a page, or search officers by name or phone"
              aria-label="Search"
              className="border-0 px-0 focus-visible:border-0"
            />
          </div>
          <Separator />

          <div className="max-h-80 overflow-y-auto p-2">
            {matches.map((entry) => {
              const Icon = entry.icon;
              return (
                <button
                  key={entry.to}
                  type="button"
                  onClick={() => go(entry.to)}
                  className={cn(
                    'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm',
                    'transition-colors hover:bg-hover focus-visible:bg-hover focus-visible:outline-none',
                  )}
                >
                  <Icon className="size-4 shrink-0 text-illustration" aria-hidden />
                  {entry.label}
                </button>
              );
            })}

            {canSearchOfficers && (
              <button
                type="button"
                onClick={() => go(`/officers?search=${encodeURIComponent(trimmed)}`)}
                className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors hover:bg-hover focus-visible:bg-hover focus-visible:outline-none"
              >
                <Search className="size-4 shrink-0 text-illustration" aria-hidden />
                Search officers for “{trimmed}”
              </button>
            )}

            {matches.length === 0 && !canSearchOfficers && (
              <EmptyState size="sm" title={trimmed ? 'Nothing matches that.' : 'Type to search.'} />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
